import { Headphones, Mail, Clock, MapPin, Phone } from "lucide-react";
import { Layout } from "@/components/layout/Layout";
import { PageHero } from "@/components/layout/PageHero";
import { Reveal } from "@/components/layout/Motion";
import { LeadForm } from "@/components/layout/LeadForm";
import { BRAND, COMPANY, EMAIL, PHONE_DISPLAY, PHONE_TEL, INDIA_ADDRESS } from "@/lib/site";

const FIELDS = [
  { name: "name", label: "Full name", required: true, placeholder: "Your name" },
  { name: "phone", label: "Phone", type: "tel", required: true, placeholder: "+1 555 123 4567" },
  { name: "email", label: "Email", type: "email", required: true, placeholder: "you@email.com" },
  { name: "subject", label: "Subject", options: ["General Enquiry", "New Booking", "Existing Booking · Change", "Existing Booking · Cancellation", "Refund Status", "Group / Corporate Travel", "Feedback / Complaint"] },
  { name: "message", label: "Message", type: "textarea", full: true, placeholder: "Tell us about your trip — dates, route, travelers, anything we should know." },
];

const INFO = [[Headphones, "Call us · 24/7", PHONE_DISPLAY, "Speak with a specialist now", PHONE_TEL], [Mail, "Email us", EMAIL, "We reply as soon as possible", `mailto:${EMAIL}`], [Clock, "Open 24 / 7", "365 days a year", "Including weekends & holidays", PHONE_TEL]];

export default function Contact() {
  return (
    <Layout>
      <PageHero testId="contact-hero" compact crumb="Contact us" line1="We'd love to hear from you" />
      <div className="mx-auto -mt-8 grid max-w-[1320px] gap-4 px-4 sm:px-8 md:grid-cols-3">
        {INFO.map(([Icon, t, v, s, href]) => (
          <Reveal key={t}><a href={href} className="relative flex items-start gap-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm hover:border-brand/40" data-testid={`contact-info-${t.split(" ")[0].toLowerCase()}`}>
            <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-brand-soft text-brand"><Icon className="h-5 w-5" /></span>
            <span><span className="block text-[11px] font-extrabold uppercase tracking-wide text-slate-500">{t}</span><span className="block break-all font-display font-bold text-ink">{v}</span><span className="text-xs text-slate-500">{s}</span></span>
          </a></Reveal>
        ))}
      </div>
      <section className="py-12">
        <div className="mx-auto grid max-w-[1320px] gap-6 px-4 sm:px-8 lg:grid-cols-[1.5fr_1fr]">
          <Reveal className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-8">
            <h2 className="font-display text-2xl font-bold text-ink">Send us a message</h2>
            <p className="mb-6 mt-1 text-sm text-slate-600">Drop your details and we'll call you back on the number you provide.</p>
            <LeadForm testId="contact-form" source="contact" fields={FIELDS} submitLabel="Send message" />
          </Reveal>
          <div className="space-y-5">
            <Reveal className="rounded-2xl border border-slate-200 bg-white p-6">
              <p className="flex items-center gap-2 text-[11px] font-extrabold uppercase tracking-wide text-brand"><MapPin className="h-4 w-4" />India office</p>
              <h3 className="mt-2 font-display text-lg font-bold text-ink">{COMPANY}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">{INDIA_ADDRESS.map((l) => <span key={l} className="block">{l}</span>)}</p>
              <a href={`mailto:${EMAIL}`} data-testid="contact-office-email" className="mt-3 flex min-w-0 items-center gap-2 text-sm font-bold text-brand"><Mail className="h-4 w-4 shrink-0" /><span className="break-all">{EMAIL}</span></a>
            </Reveal>
            <Reveal className="rounded-2xl border border-slate-200 bg-white p-6">
              <p className="flex items-center gap-2 text-[11px] font-extrabold uppercase tracking-wide text-brand"><MapPin className="h-4 w-4" />U.S. operations</p>
              <h3 className="mt-2 font-display text-lg font-bold text-ink">{BRAND} — U.S. Operations</h3>
              <p className="mt-2 text-sm text-slate-600">Toll-free phone support for U.S. travelers</p>
              <a href={PHONE_TEL} data-testid="contact-us-phone" className="mt-3 flex items-center gap-2 text-sm font-bold text-brand"><Phone className="h-4 w-4" />{PHONE_DISPLAY}</a>
            </Reveal>
            <p className="rounded-2xl bg-sand p-5 text-sm text-slate-600">Serving travelers globally through our operational offices and partner network.</p>
          </div>
        </div>
      </section>
    </Layout>
  );
}
